import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { GraduationCap, TrendingUp, Award, Users, BookOpen, CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CareerProgression = ({ showFullVersion = true }) => {
  const [levels, setLevels] = useState([]);
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [expandedLevel, setExpandedLevel] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [levelsRes, settingsRes] = await Promise.all([
        axios.get(`${API}/career-levels`),
        axios.get(`${API}/career-progression-settings`)
      ]);
      setLevels(levelsRes.data.levels?.filter(l => l.isVisible !== false) || []);
      setSettings(settingsRes.data.settings || {});
    } catch (error) {
      console.error('Error fetching career progression:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="py-12 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (levels.length === 0) {
    return null; // Don't show section if no levels
  }

  const icons = [
    <GraduationCap className="w-6 h-6" />,
    <BookOpen className="w-6 h-6" />,
    <TrendingUp className="w-6 h-6" />,
    <Users className="w-6 h-6" />,
    <Award className="w-6 h-6" />,
  ];

  const colors = [
    'bg-blue-500',
    'bg-green-500',
    'bg-purple-500',
    'bg-orange-500',
    'bg-red-500',
  ];

  return (
    <section className="py-16 bg-gradient-to-b from-gray-50 to-white" data-testid="career-progression">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            {settings.sectionTitle || 'Career Progression'}
          </h2>
          {settings.introText && (
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              {settings.introText}
            </p>
          )}
        </div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line (Desktop) */}
          <div className="hidden md:block absolute left-1/2 top-0 bottom-0 w-0.5 bg-gray-200 -translate-x-1/2"></div>

          {/* Levels */}
          <div className="space-y-8 md:space-y-0">
            {levels.map((level, index) => {
              const isEven = index % 2 === 0;
              const isExpanded = expandedLevel === level.id;
              const iconIndex = index % icons.length;
              const colorClass = colors[index % colors.length];
              
              return (
                <div 
                  key={level.id} 
                  className={`relative md:flex md:items-center ${isEven ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                  data-testid={`career-level-${level.id}`}
                >
                  {/* Timeline Node */}
                  <div className="hidden md:flex absolute left-1/2 -translate-x-1/2 w-12 h-12 rounded-full bg-white border-4 border-gray-200 items-center justify-center z-10">
                    <span className="text-lg font-bold text-gray-600">{index + 1}</span>
                  </div>

                  {/* Content Card */}
                  <div className={`md:w-5/12 ${isEven ? 'md:pr-16' : 'md:pl-16'}`}>
                    <Card 
                      className={`border-0 shadow-lg hover:shadow-xl transition-shadow cursor-pointer overflow-hidden ${isExpanded ? 'ring-2 ring-blue-500' : ''}`}
                      onClick={() => setExpandedLevel(isExpanded ? null : level.id)}
                    >
                      {/* Level Header */}
                      <div className={`${colorClass} text-white p-4 flex items-center justify-between`}>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                            {icons[iconIndex]}
                          </div>
                          <div>
                            <h3 className="text-lg font-bold">{level.title}</h3>
                            {level.durationRange && (
                              <p className="text-sm text-white/80">{level.durationRange}</p>
                            )}
                          </div>
                        </div>
                        <button className="p-1 hover:bg-white/20 rounded-full transition-colors">
                          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
                        </button>
                      </div>

                      {/* Level Content */}
                      <CardContent className="p-5">
                        {level.description && (
                          <p className="text-gray-600 mb-4">{level.description}</p>
                        )}

                        {/* Expanded Content */}
                        {isExpanded && (
                          <div className="space-y-4 pt-4 border-t animate-in slide-in-from-top-2 duration-200">
                            {/* Responsibilities */}
                            {level.responsibilities?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Key Responsibilities</h4>
                                <ul className="space-y-1.5">
                                  {level.responsibilities.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                                      <CheckCircle className="w-4 h-4 text-green-500 shrink-0 mt-0.5" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}

                            {/* Skills & Training */}
                            {level.skillsAndTraining?.length > 0 && (
                              <div>
                                <h4 className="text-sm font-semibold text-gray-900 mb-2">Skills & Training</h4>
                                <ul className="space-y-1.5">
                                  {level.skillsAndTraining.map((item, idx) => (
                                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                                      <BookOpen className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                                      {item}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            )}
                          </div>
                        )}

                        {!isExpanded && (level.responsibilities?.length > 0 || level.skillsAndTraining?.length > 0) && (
                          <p className="text-sm text-blue-600 mt-2">Click to see details</p>
                        )}
                      </CardContent>
                    </Card>
                  </div>

                  {/* Empty space for alternating layout */}
                  <div className="hidden md:block md:w-5/12"></div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Supporting Content */}
        {showFullVersion && (settings.mentoringText || settings.cpdSupportText || settings.chartershipSupportText || settings.benefits?.length > 0) && (
          <div className="mt-16 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {settings.mentoringText && (
              <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-50 to-white">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Users className="w-6 h-6 text-blue-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Mentoring</h3>
                  <p className="text-sm text-gray-600">{settings.mentoringText}</p>
                </CardContent>
              </Card>
            )}
            
            {settings.cpdSupportText && (
              <Card className="border-0 shadow-lg bg-gradient-to-br from-green-50 to-white">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <BookOpen className="w-6 h-6 text-green-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">CPD Support</h3>
                  <p className="text-sm text-gray-600">{settings.cpdSupportText}</p>
                </CardContent>
              </Card>
            )}
            
            {settings.chartershipSupportText && (
              <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-50 to-white">
                <CardContent className="p-6 text-center">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <Award className="w-6 h-6 text-purple-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-2">Chartership</h3>
                  <p className="text-sm text-gray-600">{settings.chartershipSupportText}</p>
                </CardContent>
              </Card>
            )}
            
            {settings.benefits?.length > 0 && (
              <Card className="border-0 shadow-lg bg-gradient-to-br from-orange-50 to-white">
                <CardContent className="p-6">
                  <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <CheckCircle className="w-6 h-6 text-orange-600" />
                  </div>
                  <h3 className="font-bold text-gray-900 mb-3 text-center">Benefits</h3>
                  <ul className="space-y-2">
                    {settings.benefits.slice(0, 4).map((benefit, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 text-orange-500 shrink-0 mt-0.5" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default CareerProgression;
